import { useEffect, useRef } from "react";

interface LandingVideoProps {
  src: string;
  className?: string;
  preloadMargin?: string;
}

/** Играет только пока видно: иначе ролики стартуют разом и делят канал */
export const LandingVideo = ({ src, className, preloadMargin = "600px" }: LandingVideoProps) => {
  const ref = useRef<HTMLVideoElement>(null);
  const isInView = useRef(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    const preloader = new IntersectionObserver(
      ([entry], observer) => {
        if (!entry.isIntersecting) return;
        video.preload = "auto";
        video.load();
        observer.disconnect();
      },
      { rootMargin: `${preloadMargin} 0px` }
    );

    const player = new IntersectionObserver(
      ([entry]) => {
        isInView.current = entry.isIntersecting;
        if (entry.isIntersecting) void video.play().catch(() => undefined);
        else video.pause();
      },
      { threshold: 0.1 }
    );

    const resume = () => {
      if (isInView.current && video.paused) void video.play().catch(() => undefined);
    };

    video.addEventListener("canplay", resume);
    preloader.observe(video);
    player.observe(video);

    return () => {
      video.removeEventListener("canplay", resume);
      preloader.disconnect();
      player.disconnect();
    };
  }, [preloadMargin]);

  return (
    <video
      ref={ref}
      className={className}
      src={src}
      muted
      loop
      playsInline
      preload="none"
    />
  );
};
